import { eventCountForUpNext, viewAllEvents, plusNEvents } from './upNext.locstring.json';
import { default as UpNextV2Event } from './UpNextV2Event';
import getTimeUntilEvent from '../utils/getTimeUntilEvent';
import { observer } from 'mobx-react-lite';
import { CommandBarButton, IButtonStyles } from 'office-ui-fabric-react/lib/Button';
import { logUsage } from 'owa-analytics';
import { CalendarEvent } from 'owa-calendar-types';
import { ClientItemId } from 'owa-client-ids';
import loc, { format } from 'owa-localize';
import { lazyOpenEventDetailsView, lazyOpenTimePanel } from 'owa-time-panel';
import { isTimePanelAvailable } from 'owa-time-panel/lib/utils/isTimePanelAvailable';
import { getCalendarEventReadDeepLinkUrl, getCalendarPath } from 'owa-url';
import * as React from 'react';
import {
    getCalendarEventItemAriaLabel,
    getCalendarEventItemTitle,
} from 'owa-calendar-event-attributes';

const styles = require('./UpNextV2Event.scss');
const classNames = require('classnames').bind(styles);
const UPNEXT_SOURCE = 'UpNext';
const MAX_EVENT_ROWS = 3;

export interface UpNextEventProps {
    upNextEvent: CalendarEvent;
    conflictingEvents: CalendarEvent[];
    containerStyle: IButtonStyles;
}

/**
 * Navigate to the appropriate view of the TimePanel
 * @param eventId id of the upcoming event
 */
function navigateToTimePanel(eventId: ClientItemId) {
    logUsage('TnS_UpNextClicked');

    if (!eventId) {
        lazyOpenTimePanel.importAndExecute(UPNEXT_SOURCE);
    } else {
        lazyOpenEventDetailsView.importAndExecute(UPNEXT_SOURCE, eventId);
    }
}

/**
 * Opens calendar event deep link or calendar if the eventId is null
 * @param eventId id of the upcoming event
 */
function openDeeplinkCalendar(eventId: ClientItemId) {
    if (!eventId) {
        window.open(getCalendarPath());
    } else {
        const calendarItemDeeplinkUrl = getCalendarEventReadDeepLinkUrl(eventId.Id);
        window.open(calendarItemDeeplinkUrl);
    }
}

function addEventRow(
    eventRows: JSX.Element[],
    id: string,
    name: string,
    location: string,
    eventCountString: string,
    timeUntilEvent: string
) {
    eventRows.push(
        <UpNextV2Event
            key={id}
            eventCountString={eventCountString}
            eventLocation={location}
            eventName={name}
            timeUntilEvent={timeUntilEvent}
        />
    );
}

export default observer(function UpNextV2EventList(props: UpNextEventProps) {
    const upNextEvent = props.upNextEvent;
    const eventId = upNextEvent.ItemId;
    const totalEventsCount = props.conflictingEvents.length + 1;
    const areThereConflicts = totalEventsCount > 1;
    const allUpcomingEvents = [upNextEvent, ...props.conflictingEvents];
    const timeUntilEvents = getTimeUntilEvent(upNextEvent);

    // VSO: 50521 - Add aria label for meeting conflicting scenario
    const titleString = !areThereConflicts && getCalendarEventItemTitle(upNextEvent);
    const ariaLabelString = getCalendarEventItemAriaLabel(upNextEvent);

    // If there are conflicting events, we want to navigate to TimePanel Agenda/Day view rather than event details view
    const eventIdToNavigateTo = !areThereConflicts && eventId;
    const onUpNextClicked = React.useCallback(() => {
        if (isTimePanelAvailable()) {
            navigateToTimePanel(eventIdToNavigateTo);
        } else {
            openDeeplinkCalendar(eventIdToNavigateTo);
        }
    }, [eventIdToNavigateTo]);

    const eventRows = [];
    const shouldShowViewAll = totalEventsCount > MAX_EVENT_ROWS;
    const numberOfEventsToShow = shouldShowViewAll ? MAX_EVENT_ROWS - 1 : allUpcomingEvents.length;

    // Add upcoming events
    for (let i = 0; i < numberOfEventsToShow; i++) {
        const event = allUpcomingEvents[i];
        const eventLocation = event.Location && event.Location.DisplayName;
        const eventCountString =
            areThereConflicts && format(loc(eventCountForUpNext), i + 1, totalEventsCount);

        addEventRow(
            eventRows,
            event.ItemId.Id,
            event.Subject,
            eventLocation,
            eventCountString,
            timeUntilEvents
        );
    }

    // Add View all events row
    if (shouldShowViewAll) {
        const remainingEventsCount = totalEventsCount - numberOfEventsToShow;
        addEventRow(
            eventRows,
            'viewAll',
            loc(viewAllEvents),
            null /* eventLocation */,
            format(loc(plusNEvents), remainingEventsCount),
            timeUntilEvents
        );
    }

    let allEventsFlexContainerStyle = styles.allEventsContainer;
    if (totalEventsCount == 2) {
        allEventsFlexContainerStyle = styles.allEventsContainerTwoEvents;
    } else if (totalEventsCount > 2) {
        allEventsFlexContainerStyle = styles.allEventsContainerThreeOrMoreEvents;
    }

    const buttonStyles = {
        flexContainer: allEventsFlexContainerStyle,
        ...props.containerStyle,
    };

    const buttonContainerClass = classNames(
        styles.upNextEventButtonContainer,
        areThereConflicts && styles.isWide
    );

    return (
        <CommandBarButton
            ariaLabel={ariaLabelString}
            className={buttonContainerClass}
            data-is-focusable={true}
            data-automation-id={'UpNext'}
            onClick={onUpNextClicked}
            role={'button'}
            title={titleString}
            styles={buttonStyles}>
            {eventRows}
        </CommandBarButton>
    );
});
